import type { ReactNode } from 'react';

export type ChipTone = 'accent' | 'good' | 'warn' | 'danger' | 'muted' | 'neutral';

// Pastel fill + the family's ink text, always fully rounded. `accent` is the
// one loud chip: solid ink with white text, the same black that carries every
// primary action. The `-soft` pastels never take white text — they take their
// own ink, which clears 4.5:1 on the pastel.
const toneClasses: Record<ChipTone, string> = {
  accent: 'border-transparent bg-accent text-accent-fg',
  good: 'border-transparent bg-good-soft text-good',
  warn: 'border-transparent bg-warn-soft text-warn',
  danger: 'border-transparent bg-danger-soft text-danger',
  muted: 'border-line bg-bg-sunken text-fg-muted',
  neutral: 'border-line bg-bg-sunken text-fg',
};

// The dot is the family ink on the pastel fills. On the solid-ink `accent`
// chip an ink dot would be invisible, so it flips to the chip's foreground.
const dotClasses: Record<ChipTone, string> = {
  accent: 'bg-accent-fg',
  good: 'bg-good',
  warn: 'bg-warn',
  danger: 'bg-danger',
  muted: 'bg-fg-faint',
  neutral: 'bg-fg-muted',
};

export function Chip({
  tone = 'neutral',
  dot,
  children,
}: {
  tone?: ChipTone;
  dot?: boolean;
  children: ReactNode;
}) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.16em] whitespace-nowrap ${toneClasses[tone]}`}
    >
      {dot && <span className={`size-1.5 rounded-full ${dotClasses[tone]}`} aria-hidden />}
      {children}
    </span>
  );
}
